/* eslint-disable sort-imports */
import { ComboBox } from 'src/components/ui/combobox';
import { DatePickerInput } from 'src/components/date-picker/date-picker-input';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { BulletinTextField as Field } from 'src/components/bulletins-form';
import DialogModal from 'src/components/ui/dialogModal';
import { CriticalUpdatesActionsMenu } from 'src/components/critical-organization-updates/updates-table';
import {
    CriticalOrganizationUpdatesTabs,
    UPDATE_CRITICAL_UPDATE_PATH,
    VIEW_CRITICAL_UPDATE_PATH,
} from 'src/components/critical-organization-updates/tabs';
import { RadioButton } from 'src/components/ui/radioButton';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import Spinner from 'src/components/ui/spinner';
import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CriticalOrganizationUpdatesService } from 'src/services/critical-organization-updates.service';
import {
    CRITICAL_NEWS_DOCUMENT_TYPE,
    CRITICAL_NEWS_LANGUAGE_CODES,
    CRITICAL_NEWS_LANGUAGES,
    CRITICAL_NEWS_TIME_OPTIONS,
    DEFAULT_END_TIME,
    DEFAULT_START_TIME,
    isBodyEmpty,
    LAST_MINUTE_TIME_OPTION,
    sanitizeNewsHtml,
    toNewsDate,
    toNewsTime,
} from 'src/utils/critical-organization-updates';
import {
    SYSTEM_ADMIN_COU_LEAVE_MODAL_BODY,
    SYSTEM_ADMIN_COU_LEAVE_MODAL_CANCEL,
    SYSTEM_ADMIN_COU_LEAVE_MODAL_CONFIRM,
    SYSTEM_ADMIN_COU_LEAVE_MODAL_TITLE,
} from 'src/localization/resouceKeys';
import RichTextEditor from 'src/components/critical-organization-updates/rich-text-editor';

import 'src/components/critical-organization-updates/styles.scss';

const EMPTY_ERRORS = 0;
const SCROLL_TOP = 0;
const MONTH_INDEX_OFFSET = 1;
const HEADLINE_MAX_LENGTH = 255;
const SELECT_DISTRIBUTION_RULE_PATH =
    '/dashboard/systemadmin/view-distribution-rule';
const HOURS_PER_PERIOD = 12;
const FRENCH_LANGUAGE_CODE = 2;
const PAD_LENGTH = 2;

interface ICriticalUpdateForm {
    headline: string;
    bodyCustom: string;
    language: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    distributionRule: string;
    distributionId: string;
}

interface ICriticalUpdateNavigationState {
    updateId?: string;
    selectedRule?: { distributionId: string; ruleName: string };
    formState?: ICriticalUpdateForm;
}

type CriticalUpdateField =
    | 'headline'
    | 'body'
    | 'startDate'
    | 'endDate'
    | 'endTime'
    | 'distributionRule';
type CriticalUpdateErrors = Partial<Record<CriticalUpdateField, string>>;

const parseYmdToDate = (value: string): Date | undefined => {
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day
        ? new Date(year, month - MONTH_INDEX_OFFSET, day)
        : undefined;
};

const ISO_DATE_TIME_PATTERN =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;
const US_DATE_TIME_PATTERN =
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/;
const TIME_STEP_MINUTES = 30;
const START_OF_DAY = 0;
const LAST_HOUR = 23;
const LAST_MINUTE = 59;

interface INewsDateParts {
    ymd: string;
    hours: number;
    minutes: number;
}

// Reads date/time exactly as stored (e.g. "2026-10-15T00:00:00.000Z" -> 2026-10-15 00:00)
// so the browser time zone cannot shift the saved CST value.
const parseNewsDateParts = (
    value: Date | string | null | undefined,
): INewsDateParts | null => {
    if (!value) return null;
    const raw = typeof value === 'string' ? value.trim() : value.toISOString();

    const iso = ISO_DATE_TIME_PATTERN.exec(raw);
    if (iso) {
        const [, year, month, day, hours = '0', minutes = '0'] = iso;
        return {
            ymd: `${year}-${month}-${day}`,
            hours: Number(hours),
            minutes: Number(minutes),
        };
    }

    const us = US_DATE_TIME_PATTERN.exec(raw);
    if (us) {
        const [, month, day, year, hours = '0', minutes = '0'] = us;
        return {
            ymd: `${year}-${month}-${day}`,
            hours: Number(hours),
            minutes: Number(minutes),
        };
    }

    return null;
};

const toTimeOption = ({ hours, minutes }: INewsDateParts): string => {
    if (hours === LAST_HOUR && minutes === LAST_MINUTE) {
        return LAST_MINUTE_TIME_OPTION;
    }
    const roundedMinutes =
        Math.floor(minutes / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
    const period = hours >= HOURS_PER_PERIOD ? 'PM' : 'AM';
    const hours12 = hours % HOURS_PER_PERIOD;
    return `${String(hours12).padStart(PAD_LENGTH, '0')}:${String(
        roundedMinutes,
    ).padStart(PAD_LENGTH, '0')} ${period}`;
};

const getStartOfToday = (): Date => {
    const today = new Date();
    today.setHours(START_OF_DAY, START_OF_DAY, START_OF_DAY, START_OF_DAY);
    return today;
};

const getLangguageLabel = (languageCode: number | null): string => {
    if (languageCode === FRENCH_LANGUAGE_CODE) return 'French';
    return 'English';
};

const UpdateCriticalOrganizationUpdates: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [id] = useState(() => {
        const navigationState =
            location.state as ICriticalUpdateNavigationState | null;
        return navigationState?.updateId;
    });

    const [form, setForm] = useState<ICriticalUpdateForm>({
        headline: '',
        bodyCustom: '',
        language: CRITICAL_NEWS_LANGUAGES[0],
        startDate: '',
        startTime: '00:00 AM',
        endDate: '',
        endTime: '',
        distributionRule: '',
        distributionId: '',
    });

    const [initialForm, setInitialForm] = useState<ICriticalUpdateForm>(form);
    const [fieldErrors, setFieldErrors] = useState<CriticalUpdateErrors>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState(false);
    const [isSubmitMessageVisible, setIsSubmitMessageVisible] = useState(false);
    const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);

    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.CriticalOrganizationUpdates.${key}`, { defaultValue });

    const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

    // The picker defaults to today as the earliest date, which would hide a
    // previously saved start date that is already in the past.
    const savedStartDate = parseYmdToDate(initialForm.startDate);
    const startMinDate =
        savedStartDate && savedStartDate < getStartOfToday()
            ? savedStartDate
            : undefined;

    const isFormComplete = Boolean(
        form.headline.trim() &&
            !isBodyEmpty(form.bodyCustom) &&
            form.startDate &&
            form.distributionRule,
    );

    useEffect(() => {
        // Captured before fetching so a rule chosen on the distribution rule page
        // (and any unsaved edits) is applied on top of the loaded data, not overwritten by it.
        const navigationState =
            location.state as ICriticalUpdateNavigationState | null;

        const fetchUpdateDetails = async () => {
            if (!id) {
                setIsLoading(false);
                navigate(VIEW_CRITICAL_UPDATE_PATH, { replace: true });
                return;
            }

            setIsLoading(true);
            try {
                const response =
                    await new CriticalOrganizationUpdatesService().getNewsById(
                        id,
                    );
                const newsDetail = response.data;

                const languageLabel = getLangguageLabel(
                    newsDetail.NEWSLANGUAGE,
                );
                const start = parseNewsDateParts(newsDetail.VALIDFROMDATE);
                const end = parseNewsDateParts(newsDetail.VALIDTHROUGHDATE);

                const loadedForm: ICriticalUpdateForm = {
                    headline: newsDetail.HEADLINE || '',
                    bodyCustom: newsDetail.document?.content || '',
                    language: languageLabel,
                    startDate: start?.ymd ?? '',
                    startTime: start ? toTimeOption(start) : '',
                    endDate: end?.ymd ?? '',
                    endTime: end ? toTimeOption(end) : '',
                    distributionRule: newsDetail.NAME || '',
                    distributionId: String(newsDetail.DISTRIBUTIONID || ''),
                };

                setInitialForm(loadedForm);
                setForm({
                    ...loadedForm,
                    ...navigationState?.formState,
                    ...(navigationState?.selectedRule && {
                        distributionRule: navigationState.selectedRule.ruleName,
                        distributionId:
                            navigationState.selectedRule.distributionId,
                    }),
                });
            } catch {
                setIsLoading(false);
                navigate(VIEW_CRITICAL_UPDATE_PATH, {
                    state: {
                        errorMessage: text(
                            'FetchError',
                            'Unable to load the critical organization update. Please try again later.',
                        ),
                    },
                });
                return;
            }

            setIsLoading(false);
        };

        fetchUpdateDetails();
    }, [id]);

    const updateForm = (field: keyof ICriticalUpdateForm, value: string) => {
        setForm((currentForm) => ({ ...currentForm, [field]: value }));
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[
                (field.startsWith('body')
                    ? 'body'
                    : field) as CriticalUpdateField
            ];
            if (
                field === 'startDate' ||
                field === 'startTime' ||
                field === 'endTime'
            ) {
                delete nextErrors.endDate;
            }
            if (
                field === 'startDate' ||
                field === 'startTime' ||
                field === 'endDate' ||
                field === 'endTime'
            ) {
                delete nextErrors.endTime;
            }
            return nextErrors;
        });
    };

    const validateForm = (): CriticalUpdateErrors => {
        const requiredMessage = t('Label.Field.Error.Required', {
            defaultValue: 'Field required',
        });
        const errors: CriticalUpdateErrors = {};

        if (!form.headline.trim()) errors.headline = requiredMessage;
        if (isBodyEmpty(form.bodyCustom)) errors.body = requiredMessage;
        if (!form.startDate) errors.startDate = requiredMessage;
        if (form.endDate && form.startDate && form.endDate < form.startDate) {
            errors.endDate = text(
                'EndBeforeStartError',
                'End date must be on or after the start date.',
            );
        } else if (
            form.endDate &&
            form.endDate === form.startDate &&
            toNewsTime(form.endTime, DEFAULT_END_TIME) <=
                toNewsTime(form.startTime, DEFAULT_START_TIME)
        ) {
            errors.endTime = text(
                'EndBeforeStartTimeError',
                'End time must be after the start time.',
            );
        }
        if (!form.distributionRule) errors.distributionRule = requiredMessage;

        return errors;
    };

    const showMessage = (message: string, isError: boolean) => {
        window.scrollTo(SCROLL_TOP, SCROLL_TOP);
        setSubmitError(isError);
        setSubmitMessage(message);
        setIsSubmitMessageVisible(true);
    };

    const handleSelectRule = () => {
        navigate(SELECT_DISTRIBUTION_RULE_PATH, {
            state: {
                selectMode: true,
                showSelectBanner: false,
                returnTo: UPDATE_CRITICAL_UPDATE_PATH,
                updateId: id,
                formState: form,
            },
        });
    };

    const handleCancel = () => {
        if (isDirty) {
            setIsLeaveDialogOpen(true);
            return;
        }
        navigate(VIEW_CRITICAL_UPDATE_PATH);
    };

    const handleLeaveConfirm = () => {
        setIsLeaveDialogOpen(false);
        navigate(VIEW_CRITICAL_UPDATE_PATH);
    };

    const handleDeleteRequest = () => {
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!id || isDeleteConfirming) return;

        setIsDeleteConfirming(true);
        setIsDeleteDialogOpen(false);

        try {
            await new CriticalOrganizationUpdatesService().deleteNews(id);
            navigate(VIEW_CRITICAL_UPDATE_PATH, {
                state: {
                    successMessage: text(
                        'DeleteSuccess',
                        'Critical Organization Updates have been successfully deleted.',
                    ),
                },
            });
        } catch {
            showMessage(
                text(
                    'DeleteError',
                    'Unable to delete the critical organization update. Please try again later.',
                ),
                true,
            );
        } finally {
            setIsDeleteConfirming(false);
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting || !id) return;

        const errors = validateForm();
        setFieldErrors(errors);
        if (Object.keys(errors).length > EMPTY_ERRORS) {
            showMessage(
                text(
                    'ValidationError',
                    'Please correct the highlighted fields before submitting.',
                ),
                true,
            );
            return;
        }

        setIsSubmitting(true);
        setIsSubmitMessageVisible(false);

        try {
            await new CriticalOrganizationUpdatesService().upsertNews({
                id,
                documentType: CRITICAL_NEWS_DOCUMENT_TYPE,
                headline: form.headline.trim(),
                distributionId: form.distributionId,
                validFromDate: toNewsDate(
                    form.startDate,
                    form.startTime,
                    DEFAULT_START_TIME,
                ),
                ...(form.endDate && {
                    validThroughDate: toNewsDate(
                        form.endDate,
                        form.endTime,
                        DEFAULT_END_TIME,
                    ),
                }),
                language: CRITICAL_NEWS_LANGUAGE_CODES[form.language],
                document: sanitizeNewsHtml(form.bodyCustom),
            });
            navigate(VIEW_CRITICAL_UPDATE_PATH, {
                state: {
                    successMessage: text(
                        'UpdateSuccess',
                        'Updates to Critical Organization Updates have been successfully saved.',
                    ),
                },
            });
        } catch {
            showMessage(
                text(
                    'SubmitError',
                    'Unable to update the critical organization update. Please try again later.',
                ),
                true,
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderDateTime = (
        idPrefix: string,
        label: string,
        dateField: 'startDate' | 'endDate',
        timeField: 'startTime' | 'endTime',
        placeholder: string,
        minDate?: Date,
    ) => {
        const timeError =
            timeField === 'endTime' ? fieldErrors.endTime : undefined;
        const groupError = fieldErrors[dateField] || timeError;
        return (
            <div className='c-form-field'>
                <label
                    className='c-form-field__label'
                    htmlFor={`${idPrefix}-date`}
                >
                    {label}
                    {dateField === 'startDate' && '*'}
                </label>
                <div className='critical-updates__datetime'>
                    <div>
                        <div
                            className={`critical-updates__date-picker ${
                                fieldErrors[dateField] ? 'is-error' : ''
                            }`}
                        >
                            <DatePickerInput
                                id={`${idPrefix}-date`}
                                ariaLabel={
                                    dateField === 'startDate'
                                        ? text(
                                              'StartDateAccessibleLabel',
                                              'Start date (required)',
                                          )
                                        : text(
                                              'EndDateAccessibleLabel',
                                              'End date (optional)',
                                          )
                                }
                                ariaDescribedBy={
                                    groupError ? `${idPrefix}-error` : undefined
                                }
                                hasError={Boolean(fieldErrors[dateField])}
                                required={dateField === 'startDate'}
                                selectedDate={form[dateField]}
                                handleOnClose={(value) =>
                                    updateForm(dateField, value)
                                }
                                {...(minDate && { minDate })}
                            />
                            <span className='critical-updates__date-placeholder'>
                                {!form[dateField] && placeholder}
                            </span>
                        </div>
                    </div>
                    <ComboBox
                        name={`${idPrefix}-time`}
                        ariaLabel={
                            timeField === 'startTime'
                                ? text(
                                      'StartTimeAccessibleLabel',
                                      'Start time (optional)',
                                  )
                                : text(
                                      'EndTimeAccessibleLabel',
                                      'End time (optional)',
                                  )
                        }
                        ariaDescribedBy={
                            groupError ? `${idPrefix}-error` : undefined
                        }
                        hasError={Boolean(timeError)}
                        options={CRITICAL_NEWS_TIME_OPTIONS}
                        placeholder={text('TimePlaceholder', 'Select time')}
                        selectedOption={form[timeField]}
                        onSelect={(value: string) =>
                            updateForm(timeField, value)
                        }
                    />
                </div>
                {groupError && (
                    <span className='c-error-message' id={`${idPrefix}-error`}>
                        {groupError}
                    </span>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <main className='critical-updates'>
                <div className='page-title'>
                    <h1>
                        {text('PageTitle', 'Critical Organization Updates')}
                    </h1>
                </div>
                <CriticalOrganizationUpdatesTabs active='update' />
                <section className='critical-updates__loader'>
                    <Spinner />
                </section>
            </main>
        );
    }

    return (
        <>
            <main className='critical-updates'>
                {submitMessage && (
                    <Notification
                        content={submitMessage}
                        type={
                            submitError
                                ? NotificationType.ERROR
                                : NotificationType.SUCCESS
                        }
                        visibility={isSubmitMessageVisible}
                        setVisibility={setIsSubmitMessageVisible}
                    />
                )}

                <div className='page-title'>
                    <h1>
                        {text('PageTitle', 'Critical Organization Updates')}
                    </h1>
                </div>

                <CriticalOrganizationUpdatesTabs active='update' />

                <section className='c-form-section critical-updates__card'>
                    <div className='c-form-section__content'>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '4px',
                            }}
                        >
                            <h2>
                                {text(
                                    'UpdateTitle',
                                    'Update Critical Organization Updates',
                                )}
                            </h2>
                            <CriticalUpdatesActionsMenu
                                onDelete={handleDeleteRequest}
                                variant='circle'
                            />
                        </div>
                        <p className='critical-updates__description'>
                            {text(
                                'Description',
                                'Follow the steps below to upload critical organization updates to the Dealer Extranet system.',
                            )}
                        </p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div className='critical-updates__fields'>
                                <Field
                                    id='critical-update-headline'
                                    label={text('HeadlineLabel', 'Headline')}
                                    placeholder={text(
                                        'HeadlinePlaceholder',
                                        'Enter headline here',
                                    )}
                                    maxLength={HEADLINE_MAX_LENGTH}
                                    value={form.headline}
                                    onChange={(value) =>
                                        updateForm('headline', value)
                                    }
                                    error={fieldErrors.headline}
                                />

                                <div className='c-form-field'>
                                    <label
                                        id='critical-update-body-label'
                                        className='c-form-field__label'
                                    >
                                        {text('BodyLabel', 'Body Content')}*
                                    </label>
                                    <RichTextEditor
                                        id='critical-update-body'
                                        labelledBy='critical-update-body-label'
                                        placeholder={text(
                                            'BodyPlaceholder',
                                            'Enter Critical Organization Update',
                                        )}
                                        value={form.bodyCustom}
                                        onChange={(html) =>
                                            updateForm('bodyCustom', html)
                                        }
                                        hasError={Boolean(fieldErrors.body)}
                                    />
                                    {fieldErrors.body && (
                                        <span className='c-error-message'>
                                            {fieldErrors.body}
                                        </span>
                                    )}
                                </div>

                                <div className='critical-updates__schedule'>
                                    <div className='c-form-field'>
                                        <label className='c-form-field__label'>
                                            {text('LanguageLabel', 'Language')}*
                                        </label>
                                        <RadioButton
                                            name='critical-update-language'
                                            options={CRITICAL_NEWS_LANGUAGES}
                                            labels={[
                                                text('English', 'English'),
                                                text('French', 'French'),
                                            ]}
                                            selectedOption={form.language}
                                            onSelect={(value: string) =>
                                                updateForm('language', value)
                                            }
                                        />
                                    </div>
                                    {renderDateTime(
                                        'critical-update-start',
                                        text(
                                            'StartDateLabel',
                                            'Start Date & Time (CST)',
                                        ),
                                        'startDate',
                                        'startTime',
                                        text(
                                            'StartDatePlaceholder',
                                            'Select start date',
                                        ),
                                        startMinDate,
                                    )}
                                    {renderDateTime(
                                        'critical-update-end',
                                        text(
                                            'EndDateLabel',
                                            'End Date & Time (CST)',
                                        ),
                                        'endDate',
                                        'endTime',
                                        text(
                                            'EndDatePlaceholder',
                                            'Select end date',
                                        ),
                                        parseYmdToDate(form.startDate),
                                    )}
                                </div>

                                <div className='c-form-field'>
                                    <label
                                        className='c-form-field__label'
                                        htmlFor='critical-update-distribution-rule'
                                    >
                                        {text(
                                            'DistributionLabel',
                                            'Choose Distribution Rule',
                                        )}
                                        *
                                    </label>
                                    <div className='critical-updates__distribution-control'>
                                        <div className='c-input'>
                                            <input
                                                id='critical-update-distribution-rule'
                                                className={`c-input__input ${
                                                    fieldErrors.distributionRule
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                readOnly
                                                placeholder={text(
                                                    'DistributionPlaceholder',
                                                    'No distribution rule selected',
                                                )}
                                                value={form.distributionRule}
                                            />
                                        </div>
                                        <button
                                            className='c-btn c-btn--secondary'
                                            type='button'
                                            onClick={handleSelectRule}
                                        >
                                            <span className='c-btn__text'>
                                                {text(
                                                    'SelectRule',
                                                    'Select Rule',
                                                )}
                                            </span>
                                        </button>
                                    </div>
                                    {fieldErrors.distributionRule && (
                                        <span className='c-error-message'>
                                            {fieldErrors.distributionRule}
                                        </span>
                                    )}
                                    <p className='critical-updates__helper'>
                                        {text(
                                            'DistributionHelp',
                                            'Click "Select Rule" to define the targeted recipient audience in the Dealer Extranet rules engine. You will be redirected back once chosen.',
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className='critical-updates__actions'>
                                <button
                                    className='c-btn c-btn--secondary'
                                    type='button'
                                    onClick={handleCancel}
                                >
                                    <span className='c-btn__text'>
                                        {text('Cancel', 'Cancel')}
                                    </span>
                                </button>
                                <button
                                    className='c-btn'
                                    type='submit'
                                    disabled={
                                        isSubmitting ||
                                        !isFormComplete ||
                                        !isDirty
                                    }
                                >
                                    <span className='c-btn__text'>
                                        {isSubmitting ? (
                                            <>
                                                <Spinner />
                                                {text('Updating', 'Saving...')}
                                            </>
                                        ) : (
                                            text('SaveUpdates', 'Save Updates')
                                        )}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
            <DialogModal
                titleContent={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_TITLE)}
                bodyContent={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_BODY)}
                open={isLeaveDialogOpen}
                cancelBtnLabel={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_CANCEL)}
                confirmBtnLabel={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_CONFIRM)}
                onCancelHandler={() => setIsLeaveDialogOpen(false)}
                onConfirmHandler={handleLeaveConfirm}
            />
            <DialogModal
                titleContent={text(
                    'DeleteModalTitle',
                    'Are you sure you want to delete this critical organization update?',
                )}
                bodyContent={text(
                    'DeleteModalBody',
                    'This action is permanent and cannot be undone.',
                )}
                open={isDeleteDialogOpen}
                cancelBtnLabel={t('Button.Cancel', { defaultValue: 'Cancel' })}
                confirmBtnLabel={text('DeleteConfirm', 'Yes, delete')}
                onCancelHandler={() => setIsDeleteDialogOpen(false)}
                onConfirmHandler={handleDeleteConfirm}
            />
            <div className='critical-updates__privacy-footer'>
                <FooterPrivacy />
            </div>
        </>
    );
};

export default UpdateCriticalOrganizationUpdates;
